import * as React from 'react'
import * as cn from 'classnames'
import * as styles from './Packshot.module.scss'
import { TwinHeroTextOverlay } from '../TextOverlay'
import Smile from './Smile'
import { Logo, LogoTypes } from 'DVWebNodeUI'
import BasicImage from './BasicImage'
import PlayButtonContainer from './PlayButtonContainer'
import PackshotContainer from './PackshotContainer'
import * as csm from '../../../../../utils/csm'
import { FeatureSwitches } from '../../../../../containers/Storefront';
import { StorefrontItemPlaybackAction, StorefrontLink, StorefrontItemImage, safeBool } from 'DVWebNodeUtils';

const BASELINE_PACKSHOT_IMG_WIDTH = 356
const BASELINE_TWIN_HERO_IMG_WIDTH = 920
const BASELINE_SQUARE_IMG_WIDTH = 200
const BASELINE_PORTRAIT_IMG_WIDTH = 184;

const PLAYBACK_COUNTER_PREFIX = 'DVWeb::Storefront::ServerPlayback'

interface PlaceholderProps {
  width: number
  height: number
  omitPlaceholderGraphic?: boolean
}

const Placeholder = ({ width, height, omitPlaceholderGraphic }: PlaceholderProps) => (
    <>{!omitPlaceholderGraphic && <Smile width={width} height={height} />}</>
)

export interface PackshotProps {
  enrichSingleItem: Function
  titleID: string
  isIdPlayable: boolean
  playbackAction: StorefrontItemPlaybackAction
  collectionType: string
  link: StorefrontLink
  image: StorefrontItemImage
  isLazy: boolean
  isTwinHero: boolean
  isPortrait: boolean
  isLive: boolean
  isRounded: boolean
  labelOnly: boolean
  hasPrimeSash: boolean
  showPlayButton: boolean
  showFakePlayButton: boolean
  canLivePlayback: boolean
  featureSwitches: FeatureSwitches
  title: string
  alternateText: string
  isVerticallyVisible?: boolean
  playButtonClassName?: string
  hoverIsExpanded: boolean
}

interface PackshotState {
  imageHasError: boolean
  isImageLoading: boolean
}

class Packshot extends React.PureComponent<PackshotProps, PackshotState> {
  playButtonRef: React.RefObject<HTMLAnchorElement>
  packshotRef: React.RefObject<HTMLImageElement>
  state: PackshotState

  constructor(props: PackshotProps) {
    super(props)
    this.state = {
      isImageLoading: true,
      imageHasError: false
    }
    this.handleImageLoad = this.handleImageLoad.bind(this)
    this.enrichThenPlay = this.enrichThenPlay.bind(this)

    // the click then enrich play button flow requires us to "click" on the play button,
    // to ensure that the shared player code works in exactly the same ways as otherwise
    // if we have no playback, we click on the packshot to go to detail page
    this.playButtonRef = React.createRef()
    this.packshotRef = React.createRef()
  }

  handleImageError(titleID: string) {
    return (error: React.SyntheticEvent<HTMLImageElement>) => {
      const errorId = 'dvWebClientImgLoadFailure'
      csm.count(errorId)
      csm.logError(error, `[${errorId}] the image for the following titleID is missing: ${titleID}`)
      this.setState({ imageHasError: true, isImageLoading: false })
    }
  }

  handleImageLoad() {
    this.setState({ isImageLoading: false })
  }

  enrichThenPlay(event: React.SyntheticEvent) {
    event.preventDefault()

    if (this.props.enrichSingleItem) {
      const promise = this.props.enrichSingleItem(
        this.props.titleID, safeBool(this.props.isIdPlayable)
      )
      promise.then(() => {
        if (this.props.playbackAction && this.playButtonRef) {
          // we have a playback action, start the player
          csm.count(`${PLAYBACK_COUNTER_PREFIX}::Success`)
          this.playButtonRef.current.click()
        } else if (this.packshotRef) {
          // no playback action, go to detail page
          csm.count(`${PLAYBACK_COUNTER_PREFIX}::NoAction`)
          this.packshotRef.current.click()
        }
      }, (error: Error) => {
        // fetch fails, go to detail page
        if (this.packshotRef) {
          csm.count(`${PLAYBACK_COUNTER_PREFIX}::FetchFailed`)
          csm.logError(
            error, '[DVWeb::Storefront] Error whilst trying to fetch enrichments for playback'
          )
          this.packshotRef.current.click()
        }
      })
    }
  }

  getDimensions() {
    switch (this.props.collectionType) {
      case 'TwinHero':
        return {
          width: 23,
          height: 10,
          defaultWidth: BASELINE_TWIN_HERO_IMG_WIDTH
        }
      case 'SquareCarousel':
        return {
          width: 1,
          height: 1,
          defaultWidth: BASELINE_SQUARE_IMG_WIDTH
        }
      case 'PortraitCarousel':
        return {
          width: 3,
          height: 4,
          defaultWidth: BASELINE_PORTRAIT_IMG_WIDTH
        }
      default:
        return {
          width: 16,
          height: 9,
          defaultWidth: BASELINE_PACKSHOT_IMG_WIDTH
        }
    }
  }

  render() {
    const { image, isLazy,
      playbackAction, hasPrimeSash, labelOnly,
      isLive, titleID, isRounded } = this.props
    const hasImage = !isLazy && image && image.url
    const dimensions = this.getDimensions()

    const classNames = cn(
      {
        [styles.isRounded]: isRounded,
        [styles.PlaceholderBackground]: !this.props.isPortrait,
        [styles.PortraitPackshot]: this.props.isPortrait
      },
      styles.Packshot,
      'tst-packshot'
    )

    return (
      <PackshotContainer {...this.props}
        classNames={classNames}
      >
        <Placeholder
          {...dimensions}
          omitPlaceholderGraphic={labelOnly}
        />
        {image && hasImage && !this.state.imageHasError &&
          (<div className={styles.Image}>
            <BasicImage
              key={image.url}
              url={image.url}
              alternateText={image.alternateText}
              onError={this.handleImageError(titleID)}
              onLoad={this.handleImageLoad}
              defaultWidth={dimensions.defaultWidth}
            />
          </div>)
        }
        {hasPrimeSash && <Logo use={LogoTypes.primeSash} className={cn(styles.Sash, {[styles.overlaidSash]: this.props.hoverIsExpanded})}></Logo>}
        {(isLive || safeBool(this.props.showPlayButton)) &&
          (this.props.showFakePlayButton || Boolean(playbackAction)) &&
          <PlayButtonContainer
            playbackAction={playbackAction}
            isLive={isLive}
            canLivePlayback={this.props.canLivePlayback}
            playButtonClassName={this.props.playButtonClassName}
            linkRef={this.playButtonRef}
            onClick={this.props.showFakePlayButton && this.enrichThenPlay}
            shouldFadeIn={this.props.featureSwitches && this.props.featureSwitches.lazyloadPB}
          />}
        {this.props.isTwinHero &&
          Boolean(this.props.title) && !this.props.labelOnly &&
          <TwinHeroTextOverlay title={this.props.title} text={this.props.alternateText} />}
        {this.props.isPortrait && this.props.title &&
            <div className={styles.PortraitLabelWrapper}>
              <p className={styles.PortraitLabel}>{this.props.title}</p>
            </div>}
      </PackshotContainer>
    )
  }
}

export default Packshot
